import { useState } from 'react';
import { Modal } from './Modal';
import {
  useApplyTemplateDiff,
  useTemplateDiff,
  type ApplyDiffInput,
  type TemplateDiff,
} from '../lib/templateMerge';
import { apiErrorMessage } from '../lib/api';

interface TemplateMergeBannerProps {
  projectId: number;
  canManage: boolean;
}

export function TemplateMergeBanner({ projectId, canManage }: TemplateMergeBannerProps) {
  const diffQ = useTemplateDiff(projectId);
  const [open, setOpen] = useState(false);

  if (!canManage) return null;
  if (diffQ.isLoading || diffQ.error) return null;
  const diff = diffQ.data;
  if (!diff || !diff.has_changes) return null;

  return (
    <>
      <div
        style={{
          background: '#fffaf0',
          border: '1px solid #f0c674',
          borderRadius: 10,
          padding: '12px 16px',
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 14,
        }}
      >
        <div style={{ fontSize: 13, color: '#8a5a00' }}>
          <strong>Vorlage aktualisiert</strong> — die Standard-Vorlage wurde inzwischen auf Version{' '}
          {diff.current_version} erweitert (Projekt nutzt v{diff.used_version}).
          {' '}
          {diff.added.length} neue · {diff.renamed.length} umbenannt · {diff.removed.length} entfernt.
        </div>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => setOpen(true)}>
          Änderungen prüfen
        </button>
      </div>

      {open && (
        <TemplateMergeModal
          projectId={projectId}
          diff={diff}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

interface TemplateMergeModalProps {
  projectId: number;
  diff: TemplateDiff;
  onClose: () => void;
}

function TemplateMergeModal({ projectId, diff, onClose }: TemplateMergeModalProps) {
  const applyMut = useApplyTemplateDiff(projectId);
  const defaultSectionId = diff.sections[0]?.id ?? 0;

  const [addSelection, setAddSelection] = useState<Record<number, number | false>>(
    Object.fromEntries(diff.added.map((a) => [a.template_position_id, defaultSectionId])),
  );
  const [renameSelection, setRenameSelection] = useState<Record<number, boolean>>(
    Object.fromEntries(diff.renamed.map((r) => [r.template_position_id, true])),
  );
  const [removeSelection, setRemoveSelection] = useState<Record<number, boolean>>(
    Object.fromEntries(
      diff.removed.map((r) => [r.task_id, !r.has_data]), // standardmässig nur leere zum Löschen vorgemerkt
    ),
  );

  const handleApply = () => {
    const input: ApplyDiffInput = {
      add: Object.entries(addSelection)
        .filter(([, sid]) => typeof sid === 'number' && sid > 0)
        .map(([tpid, sid]) => ({
          template_position_id: Number(tpid),
          section_id: sid as number,
        })),
      rename: Object.entries(renameSelection)
        .filter(([, v]) => v)
        .map(([k]) => Number(k)),
      remove: Object.entries(removeSelection)
        .filter(([, v]) => v)
        .map(([k]) => Number(k)),
    };
    applyMut.mutate(input, { onSuccess: onClose });
  };

  const nothingSelected =
    Object.values(addSelection).every((v) => !v) &&
    Object.values(renameSelection).every((v) => !v) &&
    Object.values(removeSelection).every((v) => !v);

  return (
    <Modal
      open
      title={`Vorlage-Differenz übernehmen (v${diff.used_version} → v${diff.current_version})`}
      width={780}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Abbrechen
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={applyMut.isPending || nothingSelected}
            onClick={handleApply}
          >
            {applyMut.isPending ? 'Wende an …' : 'Ausgewählte übernehmen'}
          </button>
        </>
      }
    >
      {applyMut.isError && (
        <div className="login-error">{apiErrorMessage(applyMut.error)}</div>
      )}

      {diff.added.length > 0 && (
        <section style={{ marginBottom: 18 }}>
          <h4 style={{ fontSize: 13, color: 'var(--color-navy)', marginBottom: 8 }}>
            Neue Positionen ({diff.added.length})
          </h4>
          {diff.added.map((a) => (
            <div
              key={a.template_position_id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 10px',
                border: '1px solid var(--color-border-soft)',
                borderRadius: 7,
                marginBottom: 6,
              }}
            >
              <input
                type="checkbox"
                checked={addSelection[a.template_position_id] !== false}
                onChange={(e) =>
                  setAddSelection((s) => ({
                    ...s,
                    [a.template_position_id]: e.target.checked ? defaultSectionId : false,
                  }))
                }
              />
              <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--color-text-soft)', width: 30 }}>
                {a.position_number || '—'}
              </span>
              <span style={{ flex: 1, fontSize: 13, fontWeight: a.is_group_header ? 600 : 400 }}>
                {a.is_group_header ? '📁 ' : ''}
                {a.title}
              </span>
              <select
                value={addSelection[a.template_position_id] || ''}
                onChange={(e) =>
                  setAddSelection((s) => ({
                    ...s,
                    [a.template_position_id]: e.target.value ? Number(e.target.value) : false,
                  }))
                }
                disabled={addSelection[a.template_position_id] === false}
                style={{ minWidth: 130 }}
              >
                {diff.sections.map((sec) => (
                  <option key={sec.id} value={sec.id}>
                    nach {sec.name}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </section>
      )}

      {diff.renamed.length > 0 && (
        <section style={{ marginBottom: 18 }}>
          <h4 style={{ fontSize: 13, color: 'var(--color-navy)', marginBottom: 8 }}>
            Umbenannte Positionen ({diff.renamed.length})
          </h4>
          {diff.renamed.map((r) => (
            <div
              key={r.task_id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 10px',
                border: '1px solid var(--color-border-soft)',
                borderRadius: 7,
                marginBottom: 6,
              }}
            >
              <input
                type="checkbox"
                checked={renameSelection[r.template_position_id] ?? false}
                onChange={(e) =>
                  setRenameSelection((s) => ({ ...s, [r.template_position_id]: e.target.checked }))
                }
              />
              <span style={{ flex: 1, fontSize: 13 }}>
                <span style={{ textDecoration: 'line-through', color: 'var(--color-text-soft)' }}>
                  {r.old_title}
                </span>
                <span style={{ margin: '0 6px', color: 'var(--color-text-soft)' }}>→</span>
                <span style={{ fontWeight: 600, color: 'var(--color-navy)' }}>{r.new_title}</span>
              </span>
              <span style={{ fontSize: 11, color: 'var(--color-text-soft)' }}>{r.section_name}</span>
            </div>
          ))}
        </section>
      )}

      {diff.removed.length > 0 && (
        <section style={{ marginBottom: 18 }}>
          <h4 style={{ fontSize: 13, color: 'var(--color-navy)', marginBottom: 8 }}>
            Aus Vorlage entfernt ({diff.removed.length})
          </h4>
          <p style={{ fontSize: 12, color: 'var(--color-text-soft)', marginBottom: 8 }}>
            Position wurde aus der Vorlage gelöscht. Ausgefüllte Aufgaben werden nur entfernt,
            wenn du sie explizit markierst.
          </p>
          {diff.removed.map((r) => (
            <div
              key={r.task_id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 10px',
                border: '1px solid var(--color-border-soft)',
                borderRadius: 7,
                marginBottom: 6,
                background: r.has_data ? 'var(--color-danger-soft)' : 'transparent',
              }}
            >
              <input
                type="checkbox"
                checked={removeSelection[r.task_id] ?? false}
                onChange={(e) =>
                  setRemoveSelection((s) => ({ ...s, [r.task_id]: e.target.checked }))
                }
              />
              <span style={{ flex: 1, fontSize: 13 }}>{r.title}</span>
              {r.has_data && (
                <span className="badge badge-delayed">enthält Daten</span>
              )}
              <span style={{ fontSize: 11, color: 'var(--color-text-soft)' }}>{r.section_name}</span>
            </div>
          ))}
        </section>
      )}
    </Modal>
  );
}
